import objectFitImages from 'object-fit-images';
import ImageBuddy from 'imagebuddy';
import ThemeWelcome from './components/testing/theme-welcome';
import SiteHeader from './components/site-header';
import BannerVideo from './components/banner-video';
import Modal from './components/modal';
import PostsListBlog from './components/posts-list/posts-list-blog';
import PostsListSearch from './components/posts-list/posts-list-search';
import { processExternalLinks } from './util/general-util';
import ScrollWatcher from './util/scroll-watcher';
import SiteEvents, { SiteEventNames } from './util/site-events';
import { loadIntersectionObserver } from './util/load-dependencies';
import { createSingleUseObserver } from './util/intersection-observer';
import { initHelloBar } from './util/hello-bar';
import ModalDefault from './components/modal-default';
import ModalVideoPopup from './components/modal-video-popup';
import EqualHeight from './util/equal-height';
import BlockFAQ from './blocks/faq';
import LogosSlider from './components/logos-silder';
import MediaSlider from './components/media-slider';
import TestimonialsSlider from './components/testimonials-slider';
import StatsSlider from './components/stats-slider';
import SocialShare from './components/social-share';
import Accordion from './components/accordion';
import SideTabs from './components/side-tabs';
import NewsletterSubmit from './components/newsletter-submit';
import MediaContentSlider from './components/media-content-slider';
import ImageGallerySlider from './components/image-gallery-slider';
import AccordionImage from './blocks/accordion-image';
import ResourceTemplate from './templates/resource';
import EventTemplate from './templates/event';
import Counter from './components/counter';
import FormFollowup from './components/form-followup';
import LocationModalSlider from './components/modal-locations';
import TextSlider from './components/text-slider';
import FormAccordion from './components/forms-accordion';
import PopupLink from './components/popup-widget';

function markOnce(el, key) {
  if (!el) return false;
  const attr = `data-init-${key}`;
  if (el.hasAttribute(attr)) return false;
  el.setAttribute(attr, '1');
  return true;
}

function primeSliderImages(root = document) {
  const selectors = [
    '.block-testimonials__testimonials-slider img',
    '.block-image-gallery__slider img',
    '.block-logos__logos-slider img',
    '.block-media__slider img',
    '.block-media-content__slider img',
    '.stats__stat-slider img'
  ];
  const imgs = root.querySelectorAll(selectors.join(','));
  imgs.forEach((img) => {
    try {

      if (img.loading === 'lazy') img.loading = 'eager';

      if (img.decoding !== 'sync') img.decoding = 'sync';

      if (img.dataset && img.dataset.src && !img.getAttribute('src')) {
        img.setAttribute('src', img.dataset.src);
      }
    } catch (_) {}
  });
}

function refreshSlidersAfterFirstPaint() {
  requestAnimationFrame(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 60);
  });
}

// Function initializes all counters by creating a new Counter instance for each element.
// The Counter class itself contains the IntersectionObserver logic and synchronization logic.
function initSequentialCounters() {
  // Select all elements designated as dynamic counters.
  const counters = document.querySelectorAll('.dinamyc-stat');
  
  if (counters.length === 0) return;

  // 1. Reset initial display text to 0. This prepares the element for animation start.
  counters.forEach((el) => {
    el.textContent = '0' + (el.getAttribute('data-suffix') || ''); 
  });

  // 2. Initialize each Counter instance.
  // The custom observer logic is removed; the class's constructor will handle 
  // checking visibility and coordinating the synchronized start time.
  counters.forEach((el) => {
    // Duration set to 2000ms, matching your original intent.
    new Counter(el, 2000); 
  });
}
class App {
  constructor() {
    this.instances = {
      components: {
        accordion: [],
        bannerVideo: null,
        blockFAQ: [],
        equalHeight: [],
        imageGallerySlider: [],
        logosSlider: [],
        mediaSlider: [],
        mediaContentSlider: [],
        formAccordion: [],
        PopupLink: [],
        modal: [],
        modalVideoPopup: [],
        newsletterSubmit: [],
        postsListBlog: [],
        postsListSearch: [],
        siteHeader: null,
        socialShare: null,
        statsSlider: [],
        testimonialsSlider: [],
        themeWelcome: null
      },
      templates: {
        resourceTemplate: null,
        eventTemplate: null
      },
      blocks: {
        accordionImage: [],
	      blockFAQ: [],
        sideTabs: []
      },
      scrollWatcher: null,
      imageBuddy: null
    };
    this.init();
    this.initComponents();
    this.initTemplates();
    this.initBlocks();
    this.initSocialShare();
  }

  init() {
    processExternalLinks({ target: '_blank', rel: 'noopener' });

    Modal.setDefaults({
      closeDuration: 400,
      closeButtonContent: `
        <svg xmlns="http://www.w3.org/2000/svg" class="modal__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      `.trim()
    });

    this.scrollWatcher = new ScrollWatcher((params) => {
      ScrollWatcher.defaultCallback(params, 100);
    });

    this.instances.imageBuddy = new ImageBuddy({ lazyLoad: false });

    ImageBuddy.on('image-loaded', (imgEl) => {
      objectFitImages(imgEl);
    });

    SiteEvents.subscribe(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE, (opts) => {
      this.instances.imageBuddy.update(opts || {});
    });

    initHelloBar((hbEl) => {
      document.body.style.marginTop = `${hbEl.offsetHeight}px`;
      const siteHeaderEl = document.querySelector('.site-header');
      if (siteHeaderEl) {
        siteHeaderEl.style.marginTop = `${hbEl.offsetHeight}px`;
      }
    });

    objectFitImages('img:not([src^="data:"])');
  }

  async initComponents() {
    const formFollowup = new FormFollowup();
    formFollowup.init();

    const themeWelcomeEl = document.querySelector('.theme-welcome');
    if (themeWelcomeEl && markOnce(themeWelcomeEl, 'theme-welcome')) {
      this.instances.themeWelcome = new ThemeWelcome(themeWelcomeEl);
    }

    const siteHeaderEl = document.querySelector('.site-header');
    if (siteHeaderEl && markOnce(siteHeaderEl, 'site-header')) {
      this.instances.components.siteHeader = new SiteHeader(siteHeaderEl);
    }

    Array.from(document.querySelectorAll('.newsletter-signup')).forEach((el) => {
      if (markOnce(el, 'newsletter')) {
        this.instances.components.newsletterSubmit.push(new NewsletterSubmit(el));
      }
    });

    const bannerCoverVideoEl = document.querySelector('.banner__cover-video');
    if (bannerCoverVideoEl && markOnce(bannerCoverVideoEl, 'banner-video')) {
      this.instances.components.bannerVideo = new BannerVideo(bannerCoverVideoEl);
    }

    Array.from(document.querySelectorAll('.posts-list--blog')).forEach((el) => {
      if (markOnce(el, 'posts-list-blog')) {
        this.instances.components.postsListBlog.push(new PostsListBlog(el));
      }
    });

    Array.from(document.querySelectorAll('.posts-list--search')).forEach((el) => {
      if (markOnce(el, 'posts-list-search')) {
        this.instances.components.postsListSearch.push(new PostsListSearch(el));
      }
    });

    Array.from(document.querySelectorAll('.js__modal--default')).forEach((el) => {
      if (markOnce(el, 'modal-default')) {
        this.instances.components.modal.push(new ModalDefault(el));
      }
    });

    Array.from(document.querySelectorAll('.js__modal--video')).forEach((el) => {
      if (markOnce(el, 'modal-video')) {
        this.instances.components.modalVideoPopup.push(new ModalVideoPopup(el));
      }
    });

    // TextSlider
    Array.from(document.querySelectorAll('[data-text-slider]')).forEach((el) => {
      if (markOnce(el, 'text-slider')) {
        new TextSlider(el);
      }
    });

    // Sliders
    Array.from(document.querySelectorAll('.block-testimonials')).forEach((wrap) => {
      const slider = wrap.querySelector('.block-testimonials__testimonials-slider');
      if (slider && markOnce(slider, 'testimonials')) {
        const prevBtn = wrap.querySelector('.custom-prev-button');
        const nextBtn = wrap.querySelector('.custom-next-button');
        this.instances.components.testimonialsSlider.push(new TestimonialsSlider(slider, prevBtn, nextBtn));
      }
    });

    Array.from(document.querySelectorAll('.block-image-gallery')).forEach((wrap) => {
      const slider = wrap.querySelector('.block-image-gallery__slider');
      if (slider && markOnce(slider, 'image-gallery')) {
        const prevBtn = wrap.querySelector('.custom-prev-button');
        const nextBtn = wrap.querySelector('.custom-next-button');
        const nav     = wrap.querySelector('.custom-nav-container-image-gallery');
        this.instances.components.imageGallerySlider.push(new ImageGallerySlider(slider, prevBtn, nextBtn, nav));
      }
    });

    Array.from(document.querySelectorAll('.block-logos')).forEach((wrap) => {
      const slider = wrap.querySelector('.block-logos__logos-slider');
      if (slider && markOnce(slider, 'logos')) {
        this.instances.components.logosSlider.push(new LogosSlider(slider));
      }
    });

    Array.from(document.querySelectorAll('.block-media')).forEach((wrap) => {
      const slider = wrap.querySelector('.block-media__slider');
      if (slider && markOnce(slider, 'media')) {
        this.instances.components.mediaSlider.push(new MediaSlider(slider));
      }
    });

    Array.from(document.querySelectorAll('.block-media-content__slider-wrapper')).forEach((wrap) => {
      const slider = wrap.querySelector('.block-media-content__slider');
      if (slider && markOnce(slider, 'media-content')) {
        this.instances.components.mediaContentSlider.push(new MediaContentSlider(slider));
      }
    });

    const socialShareEl = document.querySelector('.tmpl-single');
    if (socialShareEl && markOnce(socialShareEl, 'social-share')) {
      this.instances.components.socialShare = new SocialShare(socialShareEl);
    }

    Array.from(document.querySelectorAll('.accordion-container')).forEach((el) => {
      if (markOnce(el, 'accordion')) {
        this.instances.components.accordion.push(new Accordion(el));
      }
    });

    Array.from(document.querySelectorAll('.block-stats')).forEach((wrap) => {
      const slider = wrap.querySelector('.stats__stat-slider');
      if (slider && markOnce(slider, 'stats')) {
        const nav = wrap.querySelector('.custom-nav-container-stats');
        this.instances.components.statsSlider.push(new StatsSlider(slider, nav));
      }
    });

    Array.from(document.querySelectorAll('.equal-height')).forEach((el) => {
      if (markOnce(el, 'equal-height')) {
        this.instances.components.equalHeight.push(new EqualHeight(el));
      }
    });

    primeSliderImages(document);
    refreshSlidersAfterFirstPaint();
  }

  initTemplates() {
    const resourceTemplateEl = document.querySelector('.post-type-archive-resource');
    if (resourceTemplateEl && markOnce(resourceTemplateEl, 'tpl-resource')) {
      this.instances.templates.resourceTemplate = new ResourceTemplate(resourceTemplateEl);
    }
    const eventTemplateEl = document.querySelector('.post-type-archive-event');
    if (eventTemplateEl && markOnce(eventTemplateEl, 'tpl-event')) {
      this.instances.templates.eventTemplate = new EventTemplate(eventTemplateEl);
    }
  }

  async initBlocks() {
    Array.from(document.querySelectorAll('.block-accordion')).forEach((el) => {
      if (markOnce(el, 'block-accordion')) {
        this.instances.blocks.accordionImage.push(new AccordionImage(el));
      }
    });

    Array.from(document.querySelectorAll('.block-faq')).forEach((el) => {
      if (markOnce(el, 'block-faq')) {
        this.instances.blocks.blockFAQ.push(new BlockFAQ(el));
      }
    });

    Array.from(document.querySelectorAll('.block-side-tabs')).forEach((el) => {
      if (markOnce(el, 'block-side-tabs')) {
        this.instances.blocks.sideTabs.push(new SideTabs(el));
      }
    });

    const animateBlocks = document.querySelectorAll('.block-animate');
    if (animateBlocks && animateBlocks.length) {
      try {
        await loadIntersectionObserver();
        animateBlocks.forEach((watchEl) =>
          createSingleUseObserver(watchEl, (entry, el) => {
            el.classList.remove('block-animate');
          })
        );
      } catch (err) {
        console.error(err);
      }
    }

    Array.from(document.querySelectorAll('.block-form__accordion')).forEach((el) => {
      if (markOnce(el, 'form-accordion')) {
        this.instances.components.formAccordion.push(new FormAccordion(el));
      }
    });

    const popupLink = document.querySelector('.ufw_wb_inner');
    if (popupLink && markOnce(popupLink, 'popup-link')) {
      this.instances.components.PopupLink.push(new PopupLink(popupLink));
    }
  }

  initSocialShare() {
    const socialShareEls = document.querySelectorAll('[data-social-share]');
    Array.from(socialShareEls).forEach((el) => {
      if (markOnce(el, 'social-share-link')) {
        el.addEventListener('click', (e) => {
          const site = el.getAttribute('data-social-share');
          const shareUrl = el.getAttribute('href');
          e.preventDefault();
          if (!shareUrl) return;
          window.open(shareUrl, `${site}Share`, 'width=626,height=436');
        });
      }
    });
  }
}

export default App;

if (!window.__APP_BOOTED__) {
  window.__APP_BOOTED__ = true;
  document.addEventListener('DOMContentLoaded', () => {
    const app = new App();
    new LocationModalSlider();
    initSequentialCounters();

    primeSliderImages(document);
    refreshSlidersAfterFirstPaint();

    document.addEventListener('gform_post_render', () => app.initBlocks());
    document.addEventListener('ajaxComplete', () => app.initBlocks());
  });
}
